<script setup>
import { useNotificationsStore } from '@/app/stores/notifications.store.js';
import { Tooltip as VueTooltip } from 'floating-vue';
import { defineShortcuts } from '@/app/composables/defineShortcuts.js';
import CFKbd from '@/shared/components/CFKbd.vue';
import CFRoundedButton from '@/ui/RoundedButton/CFRoundedButton.vue';
import { computed } from 'vue';
import { useQuery } from '@tanstack/vue-query';
import api from '@/app/api/axios.js';
import { usePermissions } from '#modules/auth/composables/usePermissions.js';
import { AppPermissions } from '#modules/auth/constants/permissions.js';

const notificationStore = useNotificationsStore();
const { can } = usePermissions();

const { isPending, isError, isFetching, data } = useQuery({
    queryKey: ['notifications-unread'],
    queryFn: async () => {
        const { data } = await api.get('/general/notifications', {
            params: {
                filter: {
                    read: 0,
                },
            },
        });

        return data.data.length;
    },
    refetchOnWindowFocus: 'always',
});

const hasUnreadNotifications = computed(() => {
    if (isPending.value || isError.value || isFetching.value) {
        return false;
    }

    return data.value > 0;
});

defineShortcuts({
    shift_n: {
        handler: () => {
            if (notificationStore.isOpen) {
                notificationStore.close();
            } else {
                notificationStore.open();
            }
        },
    },
});
</script>

<template>
    <VueTooltip
        v-if="can(AppPermissions.ManageNotifications)"
        theme="dark-tooltip"
    >
        <CFRoundedButton
            icon="bell"
            color="primary"
            size="lg"
            :indicator="hasUnreadNotifications"
            @click="notificationStore.open()"
        />

        <template #popper>
            <div class="flex flex-col items-center">
                <strong>{{ $t('shortcuts.notifications') }}</strong>
                <CFKbd
                    value="Shift + N"
                    class="bg-primary text-neutrals-600 ring-primary"
                />
            </div>
        </template>
    </VueTooltip>
</template>
